import { RowButton } from "@/components/ui/pressable";
import { useEffect, useMemo, useState } from "react";
import {
  useGetIdentity,
  useListContext,
  useListFilterContext,
  type Identifier,
} from "ra-core";
import { Plus, Users } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableHeader, TableRow } from "@/components/ui/table";
import { List } from "@/components/admin/list";
import { ListPagination } from "@/components/admin/list-pagination";
import { Avatar } from "@/components/atomic-crm/contacts/Avatar";
import type { Contact } from "@/components/atomic-crm/types";
import { MobilePageChrome } from "@/components/atomic-crm/layout/MobilePageChrome";
import { ModuleSearchField } from "@/components/atomic-crm/layout/ModuleToolbar";
import { IconButton } from "@/components/ui/icon-button";
import { useIsMobile } from "@/hooks/use-mobile";
import { getClientShowPath, getPersonShowPath } from "@/app/routing";
import { buildAccountsPersonPreviewParams } from "@/modules/accounts/AccountsLeadPreviewSheet";
import {
  AccountsListTabToggle,
  AccountsModuleToolbar,
  type AccountsHubChrome,
} from "@/modules/accounts/AccountsModuleToolbar";
import { CONTACT_STATUS_CHOICES } from "@/modules/constants/contactStatus";
import { OpenMailComposeLink } from "@/modules/mail/OpenMailComposeLink";
import { CrmPhoneLink } from "@/modules/voice/CrmPhoneLink";
import {
  getContactPrimaryEmail,
  getContactPrimaryPhone,
} from "@/modules/billing/billingRecipientResolution";
import { cn } from "@/lib/utils";
import { useOrgModules } from "@/modules/useOrgModules";
import {
  GridCells,
  GridRowEnd,
  GridHeaderRow,
  GRID_LIST_ROW_CLASS,
  useDataGrid,
  useGridServerFilters,
  type GridColumn,
} from "@/modules/shared/dataGrid";

const statusLabel = (status?: string | null) =>
  CONTACT_STATUS_CHOICES.find((choice) => choice.value === status)?.label ??
  (status ? status.charAt(0).toUpperCase() + status.slice(1) : null);

const fullName = (contact: Contact) =>
  [contact.first_name, contact.last_name].filter(Boolean).join(" ").trim() ||
  "Unnamed contact";

const formatDate = (value: unknown) =>
  value
    ? new Date(String(value)).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "—";

const muted = (value: unknown) =>
  value == null || String(value).trim() === "" ? (
    <span className="text-muted-foreground">—</span>
  ) : (
    <span className="text-sm text-muted-foreground">{String(value)}</span>
  );

/** Links inside a clickable row must not open the row. */
const stop = (event: React.MouseEvent) => event.stopPropagation();

/** Every column People can show (Account only with company accounts). */
const peopleColumns = (peopleOnly: boolean): GridColumn<Contact>[] => {
  const columns: GridColumn<Contact>[] = [
    {
      id: "name",
      label: "Name",
      type: "text",
      field: "full_name",
      sortField: "last_name",
      sortable: true,
      filterable: true,
      alwaysVisible: true,
      cell: (contact) => (
        <div className="flex min-w-0 items-center gap-2.5">
          <Avatar record={contact} width={25} height={25} />
          <span className="truncate">{fullName(contact)}</span>
        </div>
      ),
    },
    {
      id: "account",
      label: "Account",
      type: "text",
      field: "company_name",
      sortable: true,
      filterable: true,
      cell: (contact) => {
        const companyId = contact.company_id as Identifier | null | undefined;
        return companyId != null ? (
          <Link
            to={getClientShowPath(companyId)}
            onClick={stop}
            className="text-sm text-muted-foreground hover:text-foreground hover:underline"
          >
            {contact.company_name || `Account #${companyId}`}
          </Link>
        ) : (
          <span className="text-sm text-muted-foreground">No account</span>
        );
      },
    },
    {
      id: "phone",
      label: "Phone",
      type: "text",
      field: "phone_fts",
      filterable: true,
      cell: (contact) => (
        <span onClick={stop}>
          <CrmPhoneLink
            phone={getContactPrimaryPhone(contact)}
            contactId={contact.id}
            className="text-sm text-muted-foreground hover:text-foreground hover:underline"
          />
        </span>
      ),
    },
    {
      id: "email",
      label: "Email",
      type: "text",
      field: "email_fts",
      filterable: true,
      cell: (contact) => {
        const email = getContactPrimaryEmail(contact);
        return email ? (
          <span onClick={stop}>
            <OpenMailComposeLink
              to={email}
              contactId={contact.id}
              companyId={
                (contact.company_id as Identifier | null | undefined) ??
                undefined
              }
              className="text-sm text-muted-foreground hover:text-foreground hover:underline"
            >
              {email}
            </OpenMailComposeLink>
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        );
      },
    },
    {
      id: "status",
      label: "Status",
      type: "select",
      sortable: true,
      filterable: true,
      options: CONTACT_STATUS_CHOICES.map((choice) => ({
        value: choice.value,
        label: choice.label,
      })),
      cell: (contact) => {
        const status = statusLabel(contact.status);
        return status ? (
          <Badge
            variant="outline"
            className={cn(
              "text-2xs font-medium",
              contact.status === "client" &&
                "border-success/40 bg-success/10 text-success-text",
            )}
          >
            {status}
          </Badge>
        ) : (
          <span className="text-muted-foreground">—</span>
        );
      },
    },
    {
      id: "title",
      label: "Title",
      type: "text",
      sortable: true,
      filterable: true,
      defaultHidden: true,
      cell: (contact) => muted(contact.title),
    },
    {
      id: "city",
      label: "City",
      type: "text",
      sortable: true,
      filterable: true,
      defaultHidden: true,
      cell: (contact) => muted((contact as { city?: string }).city),
    },
    {
      id: "lead_source",
      label: "Source",
      type: "text",
      sortable: true,
      filterable: true,
      defaultHidden: true,
      cell: (contact) => muted(contact.lead_source),
    },
    {
      id: "first_seen",
      label: "Added",
      type: "date",
      sortable: true,
      filterable: true,
      defaultHidden: true,
      cell: (contact) => muted(formatDate(contact.first_seen)),
    },
    {
      id: "last_contacted_at",
      label: "Last contacted",
      type: "date",
      sortable: true,
      filterable: true,
      defaultHidden: true,
      cell: (contact) =>
        muted(
          formatDate(
            (contact as { last_contacted_at?: string }).last_contacted_at,
          ),
        ),
    },
    {
      id: "next_followup_at",
      label: "Next follow-up",
      type: "date",
      sortable: true,
      filterable: true,
      defaultHidden: true,
      cell: (contact) =>
        muted(
          formatDate(
            (contact as { next_followup_at?: string }).next_followup_at,
          ),
        ),
    },
    {
      id: "nb_tasks",
      label: "Open tasks",
      type: "number",
      sortable: true,
      filterable: true,
      defaultHidden: true,
      align: "right",
      cell: (contact) => muted((contact as { nb_tasks?: number }).nb_tasks),
    },
  ];
  return peopleOnly ? columns.filter((c) => c.id !== "account") : columns;
};

/** Accounts → People: every contact, with or without an account. */
export const AccountsPeopleList = ({
  accountsChrome,
}: {
  accountsChrome: AccountsHubChrome;
}) => {
  const { identity } = useGetIdentity();
  if (!identity) return null;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <List
        resource="contacts"
        title={false}
        disableBreadcrumb
        perPage={25}
        sort={{ field: "last_name", order: "ASC" }}
        actions={false}
        contentScrollable={false}
        disableSyncWithLocation
        storeKey="accounts.people"
        className="min-h-0 flex-1"
        // Same 16px gap as the toolbar above the table card.
        paginationClassName="mt-4 border-t-0 bg-transparent px-0"
        pagination={<ListPagination rowsPerPageOptions={[10, 25, 50, 100]} />}
      >
        <AccountsPeopleListBody accountsChrome={accountsChrome} />
      </List>
    </div>
  );
};

const AccountsPeopleListBody = ({
  accountsChrome,
}: {
  accountsChrome: AccountsHubChrome;
}) => {
  // Accounts → People only (Apps): no company column or search.
  const peopleOnly = useOrgModules().option("accounts", "peopleOnly");
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data = [], total, isPending } = useListContext<Contact>();
  const { filterValues, setFilters } = useListFilterContext();
  const [searchDraft, setSearchDraft] = useState(() =>
    String(filterValues?.q ?? ""),
  );
  const columns = useMemo(() => peopleColumns(peopleOnly), [peopleOnly]);
  const grid = useDataGrid({ tableId: "accounts.people", columns });
  useGridServerFilters(columns, grid.filters, grid.isLoaded);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const next = searchDraft.trim();
      const current = String(filterValues?.q ?? "").trim();
      if (next === current) return;
      const nextFilters = { ...filterValues };
      if (next) nextFilters.q = next;
      else delete nextFilters.q;
      setFilters(nextFilters, undefined, false);
    }, 250);
    return () => window.clearTimeout(handle);
  }, [searchDraft, filterValues, setFilters]);

  const openPerson = (contact: Contact) => {
    if (isMobile) {
      navigate(getPersonShowPath(contact));
      return;
    }
    setSearchParams(buildAccountsPersonPreviewParams(searchParams, contact), {
      replace: true,
    });
  };

  const searchField = (
    <ModuleSearchField
      value={searchDraft}
      onChange={setSearchDraft}
      basePlaceholder={
        peopleOnly
          ? "Search name, email, or phone"
          : "Search name, email, phone, or company"
      }
      total={total}
      itemSingular="person"
      itemPlural="people"
    />
  );

  const hasFilters = Boolean(filterValues?.q) || grid.filters.length > 0;
  const emptyMessage = hasFilters
    ? "No people match these filters."
    : "No people yet.";

  if (isPending) return null;

  if (isMobile) {
    return (
      <MobilePageChrome
        title="People"
        action={
          accountsChrome.onNewContact ? (
            <IconButton
              aria-label="New contact"
              onClick={accountsChrome.onNewContact}
            >
              <Plus className="size-6" />
            </IconButton>
          ) : undefined
        }
        search={
          <div className="space-y-2">
            {accountsChrome.onListTabChange ? (
              <AccountsListTabToggle
                value={accountsChrome.listTab ?? "people"}
                onChange={accountsChrome.onListTabChange}
              />
            ) : null}
            {searchField}
            <div className="flex items-center gap-2">
              {grid.fullFilterButton}
            </div>
            {grid.chips}
          </div>
        }
      >
        {!data.length ? (
          <p className="py-12 text-center text-sm text-muted-foreground">
            {emptyMessage}
          </p>
        ) : (
          <ul className="glass-grouped">
            {data.map((contact) => (
              <li key={String(contact.id)}>
                <RowButton
                  layout="none"
                  density="none"
                  className="flex w-full items-start gap-3 px-1 py-3.5 text-left"
                  onClick={() => openPerson(contact)}
                >
                  <Avatar record={contact} width={40} height={40} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate">{fullName(contact)}</div>
                    <div className="truncate text-sm text-muted-foreground">
                      {(!peopleOnly && contact.company_name) ||
                        getContactPrimaryEmail(contact) ||
                        (peopleOnly ? "" : "No account")}
                    </div>
                  </div>
                </RowButton>
              </li>
            ))}
          </ul>
        )}
      </MobilePageChrome>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden">
      <div className="flex shrink-0 flex-col gap-2">
        <AccountsModuleToolbar
          {...accountsChrome}
          leadingExtra={
            <div className="flex min-w-0 items-center gap-2">
              {searchField}
              {grid.filterButton}
              {grid.columnsButton}
            </div>
          }
        />
        {grid.chips}
      </div>
      {!data.length ? (
        <div className="flex flex-col items-center gap-3 py-12 text-center text-sm text-muted-foreground">
          <Users className="size-8" />
          {emptyMessage}
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto rounded-xl border bg-card">
          <Table>
            <TableHeader>
              <GridHeaderRow grid={grid.header} columns={grid.visible} />
            </TableHeader>
            <TableBody>
              {data.map((contact) => (
                <TableRow
                  key={String(contact.id)}
                  className={cn("cursor-pointer", GRID_LIST_ROW_CLASS)}
                  onClick={() => openPerson(contact)}
                >
                  <GridCells columns={grid.visible} row={contact} />
                  <GridRowEnd />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
